"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { TokenPayload } from "@/src/types/auth";
import { apiFetch, MOBILE_API } from "@/src/services/api";
import { setAuthCookie, getAuthCookie, clearAuthCookie } from "@/src/utils/authCookie";

interface AuthContextValue {
  token: string | null;
  userId: number | null;
  phoneNumber: string | null;
  name: string | null;
  /** True once the profile has loaded and the buyer is a first-timer with no name yet. */
  needsName: boolean;
  /** Called after the buyer supplies their name, so the prompt stops showing. */
  setName: (name: string) => void;
  isAuthenticated: boolean;
  isHydrated: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeToken(token: string): TokenPayload | null {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload));
    return decoded as TokenPayload;
  } catch {
    return null;
  }
}

function isTokenValid(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 > Date.now();
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Cookie is shared across all *.lvh.me / *.grablocal.in subdomains so
    // the buyer stays logged in when visiting a different brand's storefront.
    const stored = getAuthCookie();
    if (stored && isTokenValid(stored)) {
      const payload = decodeToken(stored)!;
      setToken(stored);
      setUserId(payload.userId);
      setPhoneNumber(payload.phoneNumber);
      if (payload.name) setName(payload.name);
    } else if (stored) {
      clearAuthCookie();
    }
    setIsHydrated(true);
  }, []);

  // Fetch the buyer profile once authenticated. `isFirstTimeUser` + an empty
  // name is what drives the "tell us your name" prompt.
  useEffect(() => {
    if (!userId || !token) return;
    apiFetch<{ success: boolean; data: { name?: string; isFirstTimeUser?: boolean } }>(
      `${MOBILE_API}/users/${userId}`,
      { token }
    ).then((res) => {
      if (!res.success || !res.data) return;
      if (res.data.name) setName(res.data.name);
      setIsFirstTimeUser(!!res.data.isFirstTimeUser);
    }).catch(() => {});
  }, [userId, token]);

  const login = useCallback((newToken: string) => {
    const payload = decodeToken(newToken);
    if (!payload) return;
    setAuthCookie(newToken);
    setToken(newToken);
    setUserId(payload.userId);
    setPhoneNumber(payload.phoneNumber);
    if (payload.name) setName(payload.name);
  }, []);

  const logout = useCallback(() => {
    clearAuthCookie();
    setToken(null);
    setUserId(null);
    setPhoneNumber(null);
    setName(null);
    setIsFirstTimeUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        userId,
        phoneNumber,
        name,
        needsName: token !== null && isFirstTimeUser && !name?.trim(),
        setName,
        isAuthenticated: token !== null,
        isHydrated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
